import React, { useEffect, useMemo, useState } from 'react';
import { ClipboardCheck, Check, X, Loader2, AlertCircle, Clock, Search, ChevronDown, ArrowRight } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import InterpreterPreviewCard from '../../components/InterpreterPreviewCard';

const STATUS_META = {
  pending: { label: 'Pendiente', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  approved: { label: 'Aprobada', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected: { label: 'Rechazada', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
};

const LIST_FIELDS = ['active_languages', 'passive_languages', 'specialty', 'modalities'];
const FIELD_LABELS = {
  active_languages: 'Idiomas activos', passive_languages: 'Idiomas pasivos',
  specialty: 'Especialidades', modalities: 'Modalidades', bio: 'Biografía',
  city: 'Ciudad', phone: 'Teléfono', email: 'Correo', linkedin_url: 'LinkedIn',
  website: 'Sitio web', references_text: 'Referencias', full_name: 'Nombre completo',
};
const fieldLabel = (f) => FIELD_LABELS[f] || f;
const asArray = (v) => (Array.isArray(v) ? v : (v === null || v === undefined || v === '' ? [] : [v]));

function cleanHtmlText(s) {
  // Strip HTML tags + decode entities so rich-text fields (e.g. bio) show as
  // readable plain text in the diff table.
  let t = (s || '').replace(/<[^>]*>/g, ' ');
  if (typeof document !== 'undefined') {
    const el = document.createElement('textarea');
    el.innerHTML = t;
    t = el.value;
  }
  return t.replace(/&nbsp;/gi, ' ').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
}

function formatVal(v) {
  if (v === null || v === undefined || v === '') return <span className="text-charcoal-300">(vacío)</span>;
  if (Array.isArray(v)) return v.length ? v.join(', ') : <span className="text-charcoal-300">(vacío)</span>;
  if (typeof v === 'string') {
    const t = /<\/?[a-z][\s\S]*>/i.test(v) ? cleanHtmlText(v) : v;
    return t.length > 200 ? t.slice(0, 200) + '…' : t;
  }
  return String(v);
}

export default function AdminProfileEditsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [query, setQuery] = useState('');
  const [working, setWorking] = useState('');
  const [error, setError] = useState('');
  const [notes, setNotes] = useState({}); // {reqId: text}
  const [sel, setSel] = useState({}); // {reqId: {field: [values] | bool}}

  function defaultSel(r) {
    const s = {};
    Object.entries(r.changes || {}).forEach(([f, v]) => {
      s[f] = LIST_FIELDS.includes(f) ? asArray(v) : true;
    });
    return s;
  }
  const getSel = (r) => sel[r.id] || defaultSel(r);
  function toggleListValue(r, f, val) {
    const cur = getSel(r);
    const set = new Set(cur[f] || []);
    if (set.has(val)) set.delete(val); else set.add(val);
    setSel({ ...sel, [r.id]: { ...cur, [f]: [...set] } });
  }
  function toggleField(r, f) {
    const cur = getSel(r);
    setSel({ ...sel, [r.id]: { ...cur, [f]: !cur[f] } });
  }

  async function load() {
    setLoading(true); setError('');
    try {
      const { data } = await api.get('/admin/profile-edits', { params: { status: filter } });
      setRows(data || []);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error');
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.interpreter_name || '').toLowerCase().includes(q) ||
      (r.requested_by_name || '').toLowerCase().includes(q)
    );
  }, [rows, query]);

  async function review(req, action) {
    setWorking(req.id); setError('');
    try {
      const body = { admin_notes: notes[req.id] || '' };
      if (action === 'approve') {
        const cur = getSel(req);
        const approved_values = {};
        const approved_fields = {};
        Object.keys(req.changes || {}).forEach((f) => {
          if (LIST_FIELDS.includes(f)) approved_values[f] = cur[f] || [];
          else approved_fields[f] = !!cur[f];
        });
        body.approved_values = approved_values;
        body.approved_fields = approved_fields;
      }
      await api.post(`/admin/profile-edits/${req.id}/${action}`, body);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error');
    } finally { setWorking(''); }
  }

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0, all: rows.length };
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  return (
    <div data-testid="admin-profile-edits-page" className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-charcoal-800 flex items-center gap-2">
          <ClipboardCheck size={22} className="text-cmic-600" />
          Aprobaciones de perfiles
        </h1>
        <p className="text-charcoal-500 mt-1 max-w-3xl">
          Revisa los cambios solicitados por los intérpretes en sus propios perfiles.
          Solo se publicarán en el directorio público una vez aprobados.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-400" />
          <input
            type="text"
            placeholder="Buscar por nombre…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="admin-profile-edits-search"
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cmic-300"
          />
        </div>
        <div className="flex flex-wrap gap-1 text-xs">
          {['pending', 'approved', 'rejected', 'all'].map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`admin-profile-edits-filter-${k}`}
              className={`px-3 py-1.5 rounded-lg border transition-colors ${filter === k ? 'bg-cmic-500 text-white border-cmic-500' : 'bg-white text-charcoal-600 border-gray-200 hover:bg-gray-50'}`}
            >
              {k === 'all' ? `Todas (${counts.all})` : `${STATUS_META[k]?.label || k}`}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 flex items-center gap-2">
          <AlertCircle size={15} /> {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-cmic-500" size={32} /></div>
      ) : visible.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
          <ClipboardCheck size={40} className="text-charcoal-300 mx-auto mb-3" />
          <p className="text-charcoal-500">No hay solicitudes que mostrar.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {visible.map((r) => {
            const meta = STATUS_META[r.status] || STATUS_META.pending;
            const _order = ['active_languages', 'passive_languages', 'specialty', 'modalities'];
            const changedFields = Object.keys(r.changes || {}).sort((a, b) => {
              const ia = _order.indexOf(a); const ib = _order.indexOf(b);
              return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
            });
            return (
              <article key={r.id} data-testid={`admin-profile-edit-${r.id}`} className="bg-white border border-gray-200 rounded-xl p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                  <div>
                    <h3 className="font-semibold text-charcoal-800">{r.interpreter_name}</h3>
                    <div className="text-xs text-charcoal-500 mt-0.5 flex items-center gap-2 flex-wrap">
                      <span>Solicitado por <strong className="text-charcoal-700">{r.requested_by_name}</strong></span>
                      <span>·</span>
                      <span className="inline-flex items-center gap-1"><Clock size={11} />{new Date(r.created_at).toLocaleString('es-MX')}</span>
                    </div>
                  </div>
                  <span className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded border ${meta.cls}`}>{meta.label}</span>
                </div>

                {r.consolidated_count > 1 && (
                  <div data-testid={`admin-profile-edits-consolidated-${r.id}`} className="mb-4 -mt-1 inline-flex items-center gap-1.5 text-[11px] font-medium text-cmic-700 bg-cmic-50 border border-cmic-100 rounded-lg px-2.5 py-1">
                    <ClipboardCheck size={12} />
                    Incluye {r.consolidated_count} solicitudes combinadas (se muestran los últimos cambios)
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-3 mb-4">
                  <InterpreterPreviewCard
                    data={r.interpreter_full || r.snapshot}
                    label="Actual"
                    accent="gray"
                  />
                  <InterpreterPreviewCard
                    data={{ ...(r.interpreter_full || r.snapshot || {}), ...(r.changes || {}) }}
                    label="Propuesto"
                    accent="cmic"
                    highlightFields={changedFields}
                  />
                </div>

                <details className="mb-3 group" data-testid={`admin-profile-edits-diff-${r.id}`}>
                  <summary className="cursor-pointer text-xs text-charcoal-500 hover:text-charcoal-700 inline-flex items-center gap-1 list-none">
                    <ChevronDown size={12} className="transition-transform group-open:rotate-180" />
                    Ver detalle de cambios ({changedFields.length} campo{changedFields.length !== 1 && 's'})
                  </summary>
                  <div className="overflow-x-auto mt-2 border border-gray-100 rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium text-charcoal-500 w-1/4">Campo</th>
                          <th className="text-left px-3 py-2 font-medium text-charcoal-500 w-3/8">Antes</th>
                          <th className="text-center px-2 py-2 w-6"></th>
                          <th className="text-left px-3 py-2 font-medium text-cmic-700 w-3/8">Propuesto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {changedFields.map((f) => (
                          <tr key={f} className="border-b border-gray-50">
                            <td className="px-3 py-2 font-mono text-[11px] text-charcoal-700 align-top">{f}</td>
                            <td className="px-3 py-2 text-charcoal-500 whitespace-pre-wrap align-top">{formatVal(r.snapshot?.[f])}</td>
                            <td className="px-2 py-2 text-center align-top"><ArrowRight size={11} className="text-charcoal-300 inline" /></td>
                            <td className="px-3 py-2 text-cmic-700 font-medium whitespace-pre-wrap align-top">{formatVal(r.changes?.[f])}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>

                {r.status === 'pending' && (
                  <div className="mb-4 rounded-xl border border-cmic-100 bg-cmic-50/40 p-4" data-testid={`admin-profile-edits-selection-${r.id}`}>
                    <div className="text-xs font-semibold text-charcoal-700 mb-3 flex items-center gap-1.5">
                      <Check size={13} className="text-emerald-600" />
                      Selecciona qué aprobar (desmarca lo que no quieras aceptar)
                    </div>
                    <div className="space-y-3">
                      {changedFields.map((f) => {
                        const cur = getSel(r);
                        if (LIST_FIELDS.includes(f)) {
                          const proposed = asArray(r.changes?.[f]);
                          const before = asArray(r.snapshot?.[f]);
                          const newItems = proposed.filter((val) => !before.includes(val));
                          const checked = new Set(cur[f] || []);
                          if (newItems.length === 0) return null;
                          return (
                            <div key={f}>
                              <div className="text-[11px] font-medium text-charcoal-500 uppercase tracking-wide mb-1.5">{fieldLabel(f)}</div>
                              <div className="flex flex-wrap gap-2">
                                {newItems.map((val) => {
                                  const on = checked.has(val);
                                  return (
                                    <button
                                      key={val}
                                      type="button"
                                      onClick={() => toggleListValue(r, f, val)}
                                      data-testid={`admin-pe-item-${r.id}-${f}-${val}`}
                                      className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border transition-all ${on ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-white border-gray-200 text-charcoal-400 line-through'}`}
                                    >
                                      <span className={`flex items-center justify-center w-3.5 h-3.5 rounded border ${on ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'}`}>
                                        {on && <Check size={10} className="text-white" />}
                                      </span>
                                      {val}
                                      <span className="text-[9px] font-bold uppercase text-cmic-600">nuevo</span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        }
                        return (
                          <label key={f} className="flex items-start gap-2.5 cursor-pointer" data-testid={`admin-pe-field-${r.id}-${f}`}>
                            <input
                              type="checkbox"
                              checked={cur[f] !== false}
                              onChange={() => toggleField(r, f)}
                              className="mt-0.5 h-4 w-4 accent-emerald-600 cursor-pointer"
                            />
                            <span className="text-xs">
                              <span className="font-medium text-charcoal-700">{fieldLabel(f)}</span>
                              <span className="block text-charcoal-500 mt-0.5">
                                <span className="line-through text-charcoal-400">{formatVal(r.snapshot?.[f])}</span>
                                <ArrowRight size={10} className="inline mx-1 text-charcoal-300" />
                                <span className="text-cmic-700 font-medium">{formatVal(r.changes?.[f])}</span>
                              </span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                {r.status === 'pending' ? (
                  <div className="mt-4 flex flex-wrap items-end gap-3">
                    <div className="flex-1 min-w-[200px]">
                      <label className="block text-[10px] font-medium text-charcoal-500 mb-1">Notas (opcional)</label>
                      <input
                        type="text"
                        value={notes[r.id] || ''}
                        onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })}
                        placeholder="Ej: aprobado, gracias / falta agregar X"
                        data-testid={`admin-profile-edits-notes-${r.id}`}
                        className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-300"
                      />
                    </div>
                    <button
                      onClick={() => review(r, 'approve')}
                      disabled={working === r.id}
                      data-testid={`admin-profile-edits-approve-${r.id}`}
                      className="inline-flex items-center gap-1.5 bg-emerald-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-emerald-600 disabled:opacity-60"
                    >
                      {working === r.id ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Aprobar
                    </button>
                    <button
                      onClick={() => review(r, 'reject')}
                      disabled={working === r.id}
                      data-testid={`admin-profile-edits-reject-${r.id}`}
                      className="inline-flex items-center gap-1.5 bg-rose-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-rose-600 disabled:opacity-60"
                    >
                      <X size={14} /> Rechazar
                    </button>
                  </div>
                ) : (
                  <div className="mt-3 space-y-3">
                    {(() => {
                      const bd = (Array.isArray(r.decision_breakdown) && r.decision_breakdown.length)
                        ? r.decision_breakdown
                        : Object.entries(r.changes || {}).map(([field, v]) => {
                            if (LIST_FIELDS.includes(field)) {
                              const vals = asArray(v);
                              return { field, type: 'list', approved: r.status === 'approved' ? vals : [], rejected: r.status === 'approved' ? [] : vals };
                            }
                            return { field, type: 'scalar', approved: r.status === 'approved', value: v };
                          });
                      if (!bd.length) return null;
                      return (
                      <div data-testid={`admin-profile-edits-breakdown-${r.id}`} className="rounded-xl border border-gray-100 bg-gray-50/60 p-3 space-y-2">
                        <div className="text-[11px] font-semibold text-charcoal-600 uppercase tracking-wide">Desglose de la decisión</div>
                        {bd.map((b) => (
                          <div key={b.field} className="text-xs">
                            <span className="font-medium text-charcoal-700">{fieldLabel(b.field)}</span>
                            <div className="mt-1 flex flex-wrap gap-1.5">
                              {b.type === 'list' ? (
                                <>
                                  {(b.approved || []).map((v) => (
                                    <span key={`a-${v}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200">
                                      <Check size={10} /> {v}
                                    </span>
                                  ))}
                                  {(b.rejected || []).map((v) => (
                                    <span key={`r-${v}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-rose-50 text-rose-700 border border-rose-200 line-through">
                                      <X size={10} /> {v}
                                    </span>
                                  ))}
                                  {(b.approved || []).length === 0 && (b.rejected || []).length === 0 && (
                                    <span className="text-charcoal-400">(vacío)</span>
                                  )}
                                </>
                              ) : (
                                <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border ${b.approved ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200 line-through'}`}>
                                  {b.approved ? <Check size={10} /> : <X size={10} />} {b.approved ? 'Aprobado' : 'Rechazado'}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      );
                    })()}
                    <div className="text-xs text-charcoal-500">
                      Revisado por <strong className="text-charcoal-700">{r.reviewed_by}</strong>
                      {(() => {
                        const via = r.reviewed_via || ((r.reviewed_by || '').toLowerCase().includes('correo') ? 'email' : null);
                        if (!via) return null;
                        const isEmail = via === 'email';
                        return (
                          <span data-testid={`admin-profile-edits-via-${r.id}`}
                            className={`ml-1.5 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${isEmail ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-cmic-50 text-cmic-700 border-cmic-200'}`}>
                            {isEmail ? 'vía correo' : 'vía panel'}
                          </span>
                        );
                      })()}
                      {' · '}{new Date(r.reviewed_at || r.updated_at).toLocaleString('es-MX')}
                      {r.admin_notes && <div className="mt-1">Notas: {r.admin_notes}</div>}
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
